'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { AlertCircle, FileText, List, Shield } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog/dialog';

interface WaiverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: () => void;
  /** i18n namespace carrying the waiver content (e.g. 'waiver'). */
  waiverNamespace: string;
}

const SIMPLIFIED_POINTS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
const PLAIN_SECTIONS = [3, 4, 5, 6, 7, 8, 9] as const;

/**
 * Waiver dialog with simplified/full toggle. Lifted from dgr/register with
 * semantic tokens; content comes entirely from the configured i18n namespace
 * (structure: title, readCarefully, pointN.*, full.sectionN.*, acknowledgment).
 */
export function WaiverDialog({ open, onOpenChange, onAccept, waiverNamespace }: WaiverDialogProps) {
  const tWaiver = useTranslations(waiverNamespace);
  const [simplified, setSimplified] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <Shield className="h-5 w-5 text-primary" />
              {tWaiver('title')}
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            {tWaiver('readCarefully')}
          </DialogDescription>
        </DialogHeader>

        {/* Toggle for Simplified/Full Waiver */}
        <div className="flex flex-shrink-0 items-center justify-between rounded-lg border border-border bg-muted/50 px-4 py-3">
          <div className="flex items-center gap-3">
            {simplified ? (
              <List className="h-5 w-5 text-primary" />
            ) : (
              <FileText className="h-5 w-5 text-primary" />
            )}
            <div>
              <p className="text-sm font-medium text-foreground">
                {simplified ? tWaiver('simplifiedSummary') : tWaiver('fullDocument')}
              </p>
              <p className="text-xs text-muted-foreground">
                {simplified ? tWaiver('keyPoints') : tWaiver('completeText')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{tWaiver('fullLabel')}</span>
            <Switch checked={!simplified} onCheckedChange={(checked) => setSimplified(!checked)} />
            <span className="text-xs text-muted-foreground">{tWaiver('simplified')}</span>
          </div>
        </div>

        {/* Waiver Content */}
        <div className="min-h-0 flex-1 overflow-y-auto pr-2">
          {simplified ? (
            <div className="space-y-4 py-4">
              {SIMPLIFIED_POINTS.map((num) => (
                <div key={num} className="flex items-start gap-3 rounded-lg bg-muted/40 p-3">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/15">
                    <span className="text-sm font-bold text-primary">{num}</span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      {tWaiver(`point${num}.title` as 'point1.title')}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {tWaiver(`point${num}.desc` as 'point1.desc')}
                    </p>
                  </div>
                </div>
              ))}

              <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <p className="text-sm text-amber-900">{tWaiver('simplifiedNote')}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6 py-4 text-sm leading-relaxed text-muted-foreground">
              <section>
                <h3 className="mb-3 text-base font-semibold text-foreground">
                  {tWaiver('full.title')}
                </h3>
                <p>{tWaiver('full.intro')}</p>
              </section>

              <section>
                <h3 className="mb-3 text-base font-semibold text-foreground">
                  {tWaiver('full.section1.title')}
                </h3>
                <p className="mb-3">{tWaiver('full.section1.intro')}</p>
                <ul className="ml-4 list-inside list-disc space-y-1">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <li key={num}>
                      {tWaiver(`full.section1.risk${num}` as 'full.section1.risk1')}
                    </li>
                  ))}
                </ul>
                <p className="mt-3">{tWaiver('full.section1.outro')}</p>
              </section>

              <section>
                <h3 className="mb-3 text-base font-semibold text-foreground">
                  {tWaiver('full.section2.title')}
                </h3>
                <p className="mb-3">{tWaiver('full.section2.intro')}</p>
                <ul className="ml-4 list-inside list-disc space-y-1">
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <li key={num}>{tWaiver(`full.section2.rep${num}` as 'full.section2.rep1')}</li>
                  ))}
                </ul>
              </section>

              {PLAIN_SECTIONS.map((num) => (
                <section key={num}>
                  <h3 className="mb-3 text-base font-semibold text-foreground">
                    {tWaiver(`full.section${num}.title` as 'full.section3.title')}
                  </h3>
                  <p>{tWaiver(`full.section${num}.content` as 'full.section3.content')}</p>
                </section>
              ))}

              <section className="border-t border-border pt-4">
                <p className="italic text-muted-foreground">{tWaiver('full.acknowledgment')}</p>
              </section>
            </div>
          )}
        </div>

        {/* Footer with Accept Button */}
        <div className="flex flex-shrink-0 items-center justify-between border-t border-border pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground hover:text-foreground"
          >
            {tWaiver('close')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              onAccept();
              onOpenChange(false);
            }}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {tWaiver('acceptButton')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
